/**
 * SevenDayOutlookPanel Component
 * Industry-grade 7-day predictive outlook visualization
 * Shows health forecasts, scheduled events, and risk projections
 */

import { useMemo, useState } from 'react'
import Plot from 'react-plotly.js'
import {
  Clock,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Wrench,
  Shield,
  Target,
  Sun,
  Cloud,
  CloudRain,
  Zap,
  ChevronLeft,
  ChevronRight,
  Activity
} from 'lucide-react'
import clsx from 'clsx'

interface DayForecast {
  date: string
  dayName: string
  healthPrediction: number
  healthConfidence: { lower: number; upper: number }
  riskLevel: 'low' | 'medium' | 'high' | 'critical'
  anomalyProbability: number
  scheduledMaintenance: {
    id: string
    type: string
    component: string
    duration: number
  }[]
  alerts: {
    id: string
    type: 'warning' | 'critical'
    message: string
  }[]
  weatherCondition?: 'optimal' | 'moderate' | 'challenging'
}

interface TrendMetric {
  name: string
  current: number
  predicted: number
  unit: string
  trend: 'improving' | 'stable' | 'declining'
}

interface SevenDayOutlookPanelProps {
  assetId: string
  forecasts: DayForecast[]
  trendMetrics: TrendMetric[]
  overallOutlook: 'positive' | 'neutral' | 'concerning'
  confidenceLevel: number
  lastUpdated: string
  className?: string
}

export default function SevenDayOutlookPanel({
  assetId,
  forecasts,
  trendMetrics,
  overallOutlook,
  confidenceLevel,
  lastUpdated,
  className
}: SevenDayOutlookPanelProps) {
  const [selectedDay, setSelectedDay] = useState<number>(0)

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const avgHealth = forecasts.reduce((sum, f) => sum + f.healthPrediction, 0) / forecasts.length
    const criticalDays = forecasts.filter(f => f.riskLevel === 'critical' || f.riskLevel === 'high').length
    const totalMaintenance = forecasts.reduce((sum, f) => sum + f.scheduledMaintenance.length, 0)
    const totalAlerts = forecasts.reduce((sum, f) => sum + f.alerts.length, 0)
    const lowestHealth = Math.min(...forecasts.map(f => f.healthPrediction))
    const highestRiskDay = forecasts.reduce((prev, curr) => {
      const riskOrder = { critical: 4, high: 3, medium: 2, low: 1 }
      return riskOrder[curr.riskLevel] > riskOrder[prev.riskLevel] ? curr : prev
    })

    return { avgHealth, criticalDays, totalMaintenance, totalAlerts, lowestHealth, highestRiskDay }
  }, [forecasts])

  // Generate forecast chart data
  const forecastChartData = useMemo(() => {
    return {
      dates: forecasts.map(f => f.dayName.slice(0, 3)),
      health: forecasts.map(f => f.healthPrediction),
      lower: forecasts.map(f => f.healthConfidence.lower),
      upper: forecasts.map(f => f.healthConfidence.upper),
      anomaly: forecasts.map(f => f.anomalyProbability * 100)
    }
  }, [forecasts])

  const getOutlookIcon = () => {
    switch (overallOutlook) {
      case 'positive': return <Sun size={20} className="text-status-healthy" />
      case 'neutral': return <Cloud size={20} className="text-surface-400" />
      case 'concerning': return <CloudRain size={20} className="text-status-warning" />
    }
  }

  const getOutlookColor = () => {
    switch (overallOutlook) {
      case 'positive': return 'from-status-healthy/10'
      case 'neutral': return 'from-surface-600/10'
      case 'concerning': return 'from-status-warning/10'
    }
  }

  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'critical': return 'bg-status-critical text-white'
      case 'high': return 'bg-status-warning text-surface-900'
      case 'medium': return 'bg-primary-500 text-white'
      default: return 'bg-status-healthy text-white'
    }
  }

  const getRiskBgColor = (risk: string) => {
    switch (risk) {
      case 'critical': return 'border-status-critical/50 bg-status-critical/10'
      case 'high': return 'border-status-warning/50 bg-status-warning/10'
      case 'medium': return 'border-primary-500/50 bg-primary-500/10'
      default: return 'border-status-healthy/50 bg-status-healthy/10'
    }
  }

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'improving': return <TrendingUp size={14} className="text-status-healthy" />
      case 'declining': return <TrendingDown size={14} className="text-status-critical" />
      default: return <Activity size={14} className="text-surface-400" />
    }
  }

  const selectedForecast = forecasts[selectedDay]

  return (
    <div className={clsx('rounded-xl border border-surface-700 bg-surface-900 overflow-hidden', className)}>
      {/* Header */}
      <div className={clsx('p-4 bg-gradient-to-r to-transparent border-b border-surface-700', getOutlookColor())}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={clsx(
              'p-2 rounded-lg',
              overallOutlook === 'positive' ? 'bg-status-healthy/20' :
              overallOutlook === 'concerning' ? 'bg-status-warning/20' : 'bg-surface-700'
            )}>
              {getOutlookIcon()}
            </div>
            <div>
              <h3 className="font-semibold text-surface-100">7-Day Predictive Outlook</h3>
              <p className="text-xs text-surface-400">
                Asset: <span className="font-mono text-primary-400">{assetId}</span>
                <span className="mx-2">|</span>
                Updated: {lastUpdated}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-xs text-surface-400">Outlook</p>
              <p className={clsx(
                'text-lg font-semibold capitalize',
                overallOutlook === 'positive' ? 'text-status-healthy' :
                overallOutlook === 'concerning' ? 'text-status-warning' : 'text-surface-300'
              )}>
                {overallOutlook}
              </p>
            </div>
            <div className="h-10 w-px bg-surface-700" />
            <div className="text-right">
              <p className="text-xs text-surface-400">Confidence</p>
              <p className="text-lg font-semibold text-primary-400 font-mono">
                {(confidenceLevel * 100).toFixed(0)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="p-4 bg-surface-800/30 border-b border-surface-700">
        <div className="grid grid-cols-5 gap-4">
          <div className="text-center p-3 bg-surface-800/50 rounded-lg">
            <p className="text-xs text-surface-400 mb-1">Avg Health</p>
            <p className={clsx(
              'text-2xl font-bold font-mono',
              summaryStats.avgHealth >= 85 ? 'text-status-healthy' :
              summaryStats.avgHealth >= 70 ? 'text-status-warning' : 'text-status-critical'
            )}>
              {summaryStats.avgHealth.toFixed(0)}%
            </p>
          </div>
          <div className="text-center p-3 bg-surface-800/50 rounded-lg">
            <p className="text-xs text-surface-400 mb-1">Lowest Point</p>
            <p className={clsx(
              'text-2xl font-bold font-mono',
              summaryStats.lowestHealth >= 85 ? 'text-status-healthy' :
              summaryStats.lowestHealth >= 70 ? 'text-status-warning' : 'text-status-critical'
            )}>
              {summaryStats.lowestHealth.toFixed(0)}%
            </p>
          </div>
          <div className="text-center p-3 bg-surface-800/50 rounded-lg">
            <p className="text-xs text-surface-400 mb-1">High Risk Days</p>
            <p className={clsx(
              'text-2xl font-bold font-mono',
              summaryStats.criticalDays === 0 ? 'text-status-healthy' :
              summaryStats.criticalDays <= 2 ? 'text-status-warning' : 'text-status-critical'
            )}>
              {summaryStats.criticalDays}
            </p>
          </div>
          <div className="text-center p-3 bg-surface-800/50 rounded-lg">
            <p className="text-xs text-surface-400 mb-1">Scheduled Work</p>
            <p className="text-2xl font-bold font-mono text-primary-400">
              {summaryStats.totalMaintenance}
            </p>
          </div>
          <div className="text-center p-3 bg-surface-800/50 rounded-lg">
            <p className="text-xs text-surface-400 mb-1">Active Alerts</p>
            <p className={clsx(
              'text-2xl font-bold font-mono',
              summaryStats.totalAlerts === 0 ? 'text-status-healthy' : 'text-status-warning'
            )}>
              {summaryStats.totalAlerts}
            </p>
          </div>
        </div>
      </div>

      {/* Main Forecast Chart */}
      <div className="p-4 border-b border-surface-700">
        <h4 className="text-xs font-semibold text-surface-400 uppercase mb-3">Health Trajectory</h4>
        <Plot
          data={[
            // Confidence interval band
            {
              type: 'scatter',
              x: [...forecastChartData.dates, ...forecastChartData.dates.slice().reverse()],
              y: [...forecastChartData.upper, ...forecastChartData.lower.slice().reverse()],
              fill: 'toself',
              fillcolor: 'rgba(59, 130, 246, 0.1)',
              line: { color: 'transparent' },
              name: 'Confidence Range',
              hoverinfo: 'skip'
            },
            // Health prediction line
            {
              type: 'scatter',
              mode: 'lines+markers',
              x: forecastChartData.dates,
              y: forecastChartData.health,
              name: 'Predicted Health',
              line: { color: '#3b82f6', width: 3 },
              marker: {
                size: 10,
                color: forecastChartData.health.map(h =>
                  h >= 85 ? '#10b981' : h >= 70 ? '#f59e0b' : '#ef4444'
                )
              },
              hovertemplate: '%{x}<br>Health: %{y:.1f}%<extra></extra>'
            },
            // Warning threshold
            {
              type: 'scatter',
              mode: 'lines',
              x: forecastChartData.dates,
              y: forecastChartData.dates.map(() => 70),
              name: 'Warning Threshold',
              line: { color: '#f59e0b', width: 1, dash: 'dash' }
            },
            // Anomaly probability bars
            {
              type: 'bar',
              x: forecastChartData.dates,
              y: forecastChartData.anomaly,
              name: 'Anomaly Risk',
              yaxis: 'y2',
              marker: {
                color: forecastChartData.anomaly.map(a =>
                  a >= 50 ? '#ef4444' : a >= 30 ? '#f59e0b' : '#475569'
                ),
                opacity: 0.6
              },
              hovertemplate: '%{x}<br>Anomaly Risk: %{y:.0f}%<extra></extra>'
            }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ] as any}
          layout={{
            height: 280,
            margin: { t: 20, r: 50, b: 40, l: 50 },
            paper_bgcolor: 'transparent',
            plot_bgcolor: 'transparent',
            font: { color: '#94a3b8', family: 'Inter, sans-serif' },
            xaxis: {
              gridcolor: '#334155',
              tickfont: { size: 11 }
            },
            yaxis: {
              title: { text: 'Health %' },
              range: [0, 100],
              gridcolor: '#334155',
              tickfont: { size: 10 }
            },
            yaxis2: {
              title: { text: 'Risk %' },
              overlaying: 'y',
              side: 'right',
              range: [0, 100],
              showgrid: false,
              tickfont: { size: 10 }
            },
            legend: {
              orientation: 'h',
              y: -0.2,
              font: { size: 10 }
            },
            hovermode: 'x unified',
            bargap: 0.5
          }}
          config={{ displayModeBar: false, responsive: true }}
          style={{ width: '100%' }}
        />
      </div>

      {/* Day-by-Day Cards */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-semibold text-surface-400 uppercase">Daily Breakdown</h4>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setSelectedDay(Math.max(0, selectedDay - 1))}
              disabled={selectedDay === 0}
              className="p-1 rounded hover:bg-surface-700 disabled:opacity-50"
            >
              <ChevronLeft size={16} className="text-surface-400" />
            </button>
            <span className="text-xs text-surface-500 mx-2">
              {selectedDay + 1} / {forecasts.length}
            </span>
            <button
              onClick={() => setSelectedDay(Math.min(forecasts.length - 1, selectedDay + 1))}
              disabled={selectedDay === forecasts.length - 1}
              className="p-1 rounded hover:bg-surface-700 disabled:opacity-50"
            >
              <ChevronRight size={16} className="text-surface-400" />
            </button>
          </div>
        </div>

        {/* Day Cards Row */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
          {forecasts.map((forecast, index) => (
            <button
              key={forecast.date}
              onClick={() => setSelectedDay(index)}
              className={clsx(
                'flex-shrink-0 w-20 p-2 rounded-lg border text-center transition-all',
                selectedDay === index
                  ? 'border-primary-500 bg-primary-500/10'
                  : getRiskBgColor(forecast.riskLevel),
                'hover:border-primary-500/50'
              )}
            >
              <p className="text-xs text-surface-400">{forecast.dayName.slice(0, 3)}</p>
              <p className={clsx(
                'text-lg font-bold font-mono',
                forecast.healthPrediction >= 85 ? 'text-status-healthy' :
                forecast.healthPrediction >= 70 ? 'text-status-warning' : 'text-status-critical'
              )}>
                {forecast.healthPrediction}%
              </p>
              <span className={clsx(
                'inline-block px-1.5 py-0.5 text-xs rounded mt-1',
                getRiskColor(forecast.riskLevel)
              )}>
                {forecast.riskLevel.slice(0, 3)}
              </span>
            </button>
          ))}
        </div>

        {/* Selected Day Details */}
        {selectedForecast && (
          <div className={clsx(
            'p-4 rounded-lg border',
            getRiskBgColor(selectedForecast.riskLevel)
          )}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h5 className="font-semibold text-surface-200">
                  {selectedForecast.dayName}, {selectedForecast.date}
                </h5>
                <p className="text-xs text-surface-400">
                  Confidence: {selectedForecast.healthConfidence.lower}% - {selectedForecast.healthConfidence.upper}%
                </p>
              </div>
              <div className="text-right">
                <p className={clsx(
                  'text-3xl font-bold font-mono',
                  selectedForecast.healthPrediction >= 85 ? 'text-status-healthy' :
                  selectedForecast.healthPrediction >= 70 ? 'text-status-warning' : 'text-status-critical'
                )}>
                  {selectedForecast.healthPrediction}%
                </p>
                <p className="text-xs text-surface-400">predicted health</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Scheduled Maintenance */}
              <div>
                <p className="text-xs font-medium text-surface-400 mb-2 flex items-center gap-1">
                  <Wrench size={12} />
                  Scheduled Maintenance ({selectedForecast.scheduledMaintenance.length})
                </p>
                {selectedForecast.scheduledMaintenance.length > 0 ? (
                  <div className="space-y-2">
                    {selectedForecast.scheduledMaintenance.map(m => (
                      <div key={m.id} className="p-2 bg-surface-800/50 rounded text-xs">
                        <div className="flex items-center gap-2">
                          {m.type === 'preventive' ? (
                            <Shield size={12} className="text-status-healthy" />
                          ) : (
                            <Target size={12} className="text-primary-400" />
                          )}
                          <span className="text-surface-200">{m.component}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-surface-500">
                          <Clock size={10} />
                          {m.duration}h • {m.type}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-surface-500 p-2 bg-surface-800/50 rounded">
                    No maintenance scheduled
                  </p>
                )}
              </div>

              {/* Alerts */}
              <div>
                <p className="text-xs font-medium text-surface-400 mb-2 flex items-center gap-1">
                  <AlertTriangle size={12} />
                  Predicted Alerts ({selectedForecast.alerts.length})
                </p>
                {selectedForecast.alerts.length > 0 ? (
                  <div className="space-y-2">
                    {selectedForecast.alerts.map(alert => (
                      <div
                        key={alert.id}
                        className={clsx(
                          'p-2 rounded text-xs',
                          alert.type === 'critical' ? 'bg-status-critical/20' : 'bg-status-warning/20'
                        )}
                      >
                        <div className="flex items-center gap-2">
                          {alert.type === 'critical' ? (
                            <Zap size={12} className="text-status-critical" />
                          ) : (
                            <AlertTriangle size={12} className="text-status-warning" />
                          )}
                          <span className={
                            alert.type === 'critical' ? 'text-status-critical' : 'text-status-warning'
                          }>
                            {alert.message}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-2 bg-status-healthy/10 rounded text-xs flex items-center gap-2">
                    <CheckCircle size={12} className="text-status-healthy" />
                    <span className="text-status-healthy">No alerts predicted</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Trend Metrics */}
      <div className="p-4 bg-surface-800/30 border-t border-surface-700">
        <h4 className="text-xs font-semibold text-surface-400 uppercase mb-3">7-Day Trend Analysis</h4>
        <div className="grid grid-cols-4 gap-3">
          {trendMetrics.map(metric => (
            <div key={metric.name} className="p-3 bg-surface-800/50 rounded-lg border border-surface-700">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-surface-400">{metric.name}</span>
                {getTrendIcon(metric.trend)}
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-lg font-bold text-surface-100 font-mono">
                  {metric.current.toFixed(1)}
                </span>
                <span className="text-xs text-surface-500">{metric.unit}</span>
              </div>
              <div className="flex items-center gap-2 mt-1 text-xs">
                <span className="text-surface-500">→</span>
                <span className={clsx(
                  'font-mono',
                  metric.trend === 'improving' ? 'text-status-healthy' :
                  metric.trend === 'declining' ? 'text-status-critical' : 'text-surface-400'
                )}>
                  {metric.predicted.toFixed(1)} {metric.unit}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
